import { mean } from "@ciq/shared/utils/math"

/**
 * Generate a quadratic bezier curve between two points.
 * Curve bows perpendicular to the line connecting start and end.
 *
 * @param {number[]} start - [lng, lat]
 * @param {number[]} end - [lng, lat]
 * @param {number} curveFactor - How much the curve bows (0 = straight, 0.2 = moderate)
 * @param {number} segments - Number of line segments (more = smoother)
 * @returns {number[][]} Array of [lng, lat] coordinate pairs
 */
export function calculateBezierCurve(start, end, curveFactor, segments) {
  const midX = (start[0] + end[0]) / 2
  const midY = (start[1] + end[1]) / 2

  // Perpendicular offset for the control point
  const dx = end[0] - start[0]
  const dy = end[1] - start[1]
  const controlX = midX - dy * curveFactor
  const controlY = midY + dx * curveFactor

  const points = []
  for (let i = 0; i <= segments; i++) {
    const t = i / segments
    const oneMinusT = 1 - t

    // Quadratic bezier: B(t) = (1-t)^2 * P0 + 2(1-t)t * P1 + t^2 * P2
    const x = oneMinusT * oneMinusT * start[0] + 2 * oneMinusT * t * controlX + t * t * end[0]
    const y = oneMinusT * oneMinusT * start[1] + 2 * oneMinusT * t * controlY + t * t * end[1]
    points.push([x, y])
  }

  return points
}

/**
 * Normalize a volume value to the 0-1 range.
 *
 * @param {number} volume - The volume to normalize
 * @param {number} maxVolume - The maximum volume in the dataset
 * @returns {number} Normalized value between 0 and 1
 */
export function normalizeVolume(volume, maxVolume) {
  if (maxVolume <= 0) return 0
  return Math.min(1, Math.max(0, volume / maxVolume))
}

/**
 * Get the display volume for a route at a specific day/hour.
 * Returns semester volume if no day specified, day sum if no hour, or specific hour value.
 *
 * @param {Object} route - Route object with hourly_data[dow].volume[] and semester_data.volume
 * @param {string|null} day - Day of week ("1"-"7") or null for semester total
 * @param {number|null} hour - Hour (0-23) or null for full day
 * @returns {number} Volume value
 */
export function getRouteVolumeForTime(route, day, hour) {
  if (day == null) return route.semester_data?.volume ?? 0

  const dayData = route.hourly_data?.[String(day)]
  if (!dayData?.volume) return 0

  if (hour == null) {
    return dayData.volume.reduce((sum, h) => sum + h, 0)
  }

  return dayData.volume[hour] ?? 0
}

/**
 * Filter routes by percentile threshold, optionally scoped to a specific building.
 *
 * In semester mode (no displayVolumeFn): uses pre-baked semester_data.percentile / from_percentile / to_percentile.
 * In temporal mode (displayVolumeFn provided): reads backend-provided hourly percentile arrays
 * and averages them via mean() across selected days. Routes missing hourly_data for the
 * selected days are excluded (avgPercentile defaults to 0). No runtime percentile derivation
 * from volumes is performed — all percentiles come from the backend.
 *
 * @param {Array} routes - Route objects with semester_data and hourly_data
 * @param {number} threshold - Minimum percentile (0-95). 0 means show all.
 * @param {string|null} buildingId - If set, only show routes involving this building
 * @param {Function|null} displayVolumeFn - If provided, indicates temporal (non-semester) mode.
 * @param {Array|null} days - Selected days of week (e.g., ["1","2"]) for temporal filtering
 * @param {number|null} hour - Selected hour (0-23) for temporal filtering
 * @returns {Array} Filtered routes
 */
export function filterRoutesByThreshold(routes, threshold, buildingId = null, displayVolumeFn = null, days = null, hour = null) {
  if (threshold === 0 && !buildingId) return routes

  // Narrow to routes involving the selected building
  let candidateRoutes = routes
  if (buildingId) {
    candidateRoutes = routes.filter(
      r => r.start_building_id === buildingId || r.end_building_id === buildingId
    )
  }

  if (threshold === 0) return candidateRoutes

  // Temporal mode: use backend-provided hourly percentile arrays
  if (displayVolumeFn) {
    const selectedDays = (!days?.length || days.length >= 7)
      ? ["1", "2", "3", "4", "5", "6", "7"]
      : days.map(String)
    const selectedHour = hour != null ? Number(hour) : null

    return candidateRoutes.filter(route => {
      const percentileKey = buildingId
        ? (route.start_building_id === buildingId ? "from_percentile" : "to_percentile")
        : "percentile"

      const values = selectedDays
        .map(day => {
          const dayData = route.hourly_data?.[day]
          if (!dayData?.[percentileKey]) return null
          if (selectedHour != null) return dayData[percentileKey][selectedHour] ?? null
          return mean(dayData[percentileKey])
        })
        .filter(v => v != null)

      const avgPercentile = values.length > 0 ? mean(values) : 0
      return avgPercentile >= threshold
    })
  }

  // Semester mode: use pre-baked percentiles
  return candidateRoutes.filter(route => {
    if (buildingId) {
      const isOrigin = route.start_building_id === buildingId
      return isOrigin
        ? (route.semester_data?.from_percentile ?? 0) >= threshold
        : (route.semester_data?.to_percentile ?? 0) >= threshold
    }
    return (route.semester_data?.percentile ?? 0) >= threshold
  })
}

export const FLOW_CONSTANTS = {
  COLOR: "#E91E9E",
  OPACITY: 0.8,
  DASH_PATTERN: [4, 4],
  MIN_WIDTH: 0.5,
  MAX_WIDTH: 3,
  CURVE_FACTOR: 0.2,
  CURVE_SEGMENTS: 20,
  ANIMATION_FPS: 12,
}

/**
 * Build a display volume function for temporal filtering modes.
 * Returns null in semester mode (all 7 days, no hour = use pre-baked percentiles).
 * Returns a function that sums volumes across selected days/hour for temporal modes.
 *
 * @param {Array|null} days - Selected days of week (e.g., [1,2,3,4,5])
 * @param {number|null} hour - Selected hour (0-23) or null for full day
 * @returns {Function|null} Volume function or null
 */
export function buildDisplayVolumeFn(days, hour) {
  const isAllDays = !days?.length || days.length >= 7
  const isSemesterMode = isAllDays && hour == null
  if (isSemesterMode) return null

  const selectedHour = hour != null ? Number(hour) : null
  const daysToSum = isAllDays
    ? ["1", "2", "3", "4", "5", "6", "7"]
    : days.map(String)

  return route => {
    let total = 0
    for (const day of daysToSum) {
      total += getRouteVolumeForTime(route, day, selectedHour)
    }
    return total
  }
}

const EMPTY_GEOJSON = { type: "FeatureCollection", features: [] }

/**
 * Build filtered flow GeoJSON from campus flow data and current filter state.
 * Extracted as a pure function for testability.
 *
 * @param {Object|null} campusFlowData - Raw campus flow data ({ routes, maxVolume })
 * @param {boolean} campusFlow - Whether campus flow toggle is on
 * @param {number} flowThreshold - Percentile threshold (0-95)
 * @param {string|null} flowBuilding - Selected building ID or null
 * @param {Array|null} days - Selected days of week
 * @param {number|null} hour - Selected hour
 * @param {Object} buildingCentroids - Building centroid lookup { [id]: { centroid, name } }
 * @returns {Object} GeoJSON FeatureCollection
 */
export function buildFilteredFlowGeoJSON(campusFlowData, campusFlow, flowThreshold, flowBuilding, days, hour, buildingCentroids = {}) {
  if (!campusFlow || !campusFlowData?.routes?.length) return EMPTY_GEOJSON

  const displayVolumeFn = buildDisplayVolumeFn(days, hour)
  const filteredRoutes = filterRoutesByThreshold(
    campusFlowData.routes,
    flowThreshold,
    flowBuilding,
    displayVolumeFn,
    days,
    hour
  )

  return buildFlowGeoJSON(
    filteredRoutes,
    buildingCentroids,
    campusFlowData.maxVolume || 1,
    displayVolumeFn
  )
}

/**
 * Build a GeoJSON FeatureCollection from route data and building centroids.
 * Each route becomes a curved LineString feature.
 *
 * @param {Array} routes - Route objects with id, start_building_id, end_building_id, semester_data, etc.
 * @param {Object} buildings - Map of building ID to { centroid: [lng, lat], name? }
 * @param {number} maxVolume - Maximum volume in the dataset (for normalization in semester mode)
 * @param {Function|null} displayVolumeFn - If provided, use this to compute per-route volume
 *   for normalization (for day/hour temporal modes). Falls back to route.semester_data.volume when null.
 * @returns {Object} GeoJSON FeatureCollection
 */
export function buildFlowGeoJSON(routes, buildings, maxVolume, displayVolumeFn = null) {
  // When using temporal volumes, derive maxVolume from the filtered routes
  let effectiveMaxVolume = maxVolume
  if (displayVolumeFn) {
    effectiveMaxVolume = routes.reduce(
      (max, route) => Math.max(max, displayVolumeFn(route)), 0
    ) || 1
  }

  const features = []

  for (const route of routes) {
    const originBuilding = buildings[route.start_building_id]
    const destBuilding = buildings[route.end_building_id]

    if (!originBuilding?.centroid || !destBuilding?.centroid) continue

    const curveFactor = FLOW_CONSTANTS.CURVE_FACTOR

    const coordinates = calculateBezierCurve(
      originBuilding.centroid,
      destBuilding.centroid,
      curveFactor,
      FLOW_CONSTANTS.CURVE_SEGMENTS
    )

    const volume = displayVolumeFn ? displayVolumeFn(route) : (route.semester_data?.volume ?? 0)
    const normalizedVol = normalizeVolume(volume, effectiveMaxVolume)

    features.push({
      type: "Feature",
      id: route.id,
      geometry: {
        type: "LineString",
        coordinates,
      },
      properties: {
        id: route.id,
        origin: route.start_building_id,
        destination: route.end_building_id,
        originName: originBuilding.name || "",
        destinationName: destBuilding.name || "",
        volume,
        normalizedVolume: normalizedVol,
        percentile: route.semester_data?.percentile ?? 0,
        fromBuildingPercentile: route.semester_data?.from_percentile ?? 0,
        toBuildingPercentile: route.semester_data?.to_percentile ?? 0,
      },
    })
  }

  return {
    type: "FeatureCollection",
    features,
  }
}
